import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

interface Option {
  value: string
  label: string
}

interface FormSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  options: Option[]
  placeholder?: string
  required?: boolean
  // Lets the user pick the placeholder again to clear an optional field.
  clearable?: boolean
}

export function FormSelect<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  options,
  placeholder = 'Select…',
  required,
  clearable,
}: FormSelectProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })

  return (
    <div className="mb-3">
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      <select
        {...field}
        value={field.value ?? ''}
        id={name}
        className={`form-select ${error ? 'is-invalid' : ''}`.trim()}
      >
        <option value="" disabled={!clearable}>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <div className="invalid-feedback d-block">{error.message}</div>}
    </div>
  )
}
